import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-on-sign-event',
  templateUrl: './on-sign-event.component.html',
  styleUrls: ['./on-sign-event.component.scss']
})
export class OnSignEventComponent implements OnInit {
  /** What DocuSign reported via the `event` query param (signing_complete, ttl_expired, cancel, …). */
  outcome: 'signed' | 'expired' | 'cancelled' | 'declined' | 'failed' | 'unknown' = 'unknown';
  /** While true, the UI shows a "generating a new link" loader before auto-redirecting. */
  regenerating = false;
  /** Set when regeneration itself failed — drives the error UI + manual retry. */
  regenerateError = false;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const mission = params['mission'];
      const candidate = params['candidate'];
      const establishment = params['establishment'];
      const event = params['event']; // DocuSign appends this to the returnUrl
      // Set to 1 by the backend regenerate endpoint to break any potential
      // expired → regenerate → expired loop.
      const alreadyRegenerated = params['regenerated'] === '1';

      this.outcome = this.classify(event);

      // No `event` param means the page was opened from a Collary entry point
      // (chat button, email "Signer" link, etc.) rather than as a DocuSign returnUrl.
      // DocuSign embedded URLs are single-use, so we must mint a fresh one on EVERY
      // entry — otherwise the candidate's second click hits "Invalid Managed Token ID".
      // Set outcome to 'expired' so the existing 'expired' switch branch (which already
      // gates its message on regenerateError) drives the UI consistently if regen fails.
      if (!event && mission && candidate && establishment && !alreadyRegenerated) {
        this.outcome = 'expired';
        this.regenerateAndRedirect(mission, candidate, establishment);
        return;
      }

      // CRITICAL: only mark the contract as signed when DocuSign explicitly reported
      // signing_complete. Any other event (ttl_expired, session_timeout, cancel, decline,
      // viewing_complete, …) hits the same returnUrl but means the candidate did NOT sign —
      // calling the backend in those cases would wrongly flip the tracker to SIGNED.
      if (this.outcome === 'signed') {
        if (mission && candidate && establishment) {
          this.http.get(`${environment.api.BASE_URL}/docusign/event?documentId=${mission}&candidate=${candidate}&establishmentId=${establishment}`)
            .subscribe({
              next: () => {
                console.log('Document signing event processed successfully');
                setTimeout(() => window.close(), 20000);
              },
              error: (error) => console.error('Error processing document signing event:', error)
            });
        }
        return;
      }

      // Auto-regenerate a fresh sign URL when the previous embedded session timed out.
      // Skip if we already regenerated once for this redirect chain — that means the
      // new link ALSO came back expired, which is anomalous and we'd loop otherwise.
      if (this.outcome === 'expired' && mission && candidate && establishment && !alreadyRegenerated) {
        this.regenerateAndRedirect(mission, candidate, establishment);
        return;
      }

      console.warn('DocuSign returnUrl event=', event, '— no action taken.');
    });
  }

  private regenerateAndRedirect(missionId: string, candidatId: string, establishmentId: string): void {
    this.regenerating = true;
    this.regenerateError = false;
    const url = `${environment.api.BASE_URL}/docusign/regenerate-sign-url`
      + `?missionId=${missionId}&candidatId=${candidatId}&establishmentId=${establishmentId}`;
    this.http.get<{ url: string }>(url).subscribe({
      next: (res) => {
        if (res && res.url) {
          // Replace current entry so the back button doesn't bounce the user back here.
          window.location.replace(res.url);
        } else {
          this.regenerating = false;
          this.regenerateError = true;
        }
      },
      error: (err) => {
        console.error('Failed to regenerate sign URL:', err);
        this.regenerating = false;
        this.regenerateError = true;
      }
    });
  }

  private classify(event: string | undefined): 'signed' | 'expired' | 'cancelled' | 'declined' | 'failed' | 'unknown' {
    switch (event) {
      case 'signing_complete': return 'signed';
      case 'ttl_expired':
      case 'session_timeout': return 'expired';
      case 'cancel': return 'cancelled';
      case 'decline': return 'declined';
      case 'exception':
      case 'access_code_failed':
      case 'id_check_failed':
      case 'fax_pending': return 'failed';
      default: return 'unknown';
    }
  }
}